import {
  Box,
  Button,
  Divider,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Heading,
  Flex,
} from '@chakra-ui/react';
import { Column, Row } from '@tanstack/react-table';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';

import {
  utils as commonUtils,
  date as commonDate,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { DateRangePicker } from 'packages/common-frontend/components/DateRangePicker';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons/fontAwesome';

const CheckboxFilters = ({
  optionValues,
  selectedOptions,
  setSelectedOptions,
  column,
}: {
  optionValues: {
    label: any;
    value: any;
  }[];
  selectedOptions: string[];
  column: Column<any, unknown>;
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  return (
    <Box maxH="250" overflow="auto" color={colors.aurora[400]}>
      {optionValues.map(({ label, value }) => {
        return (
          <MenuItem
            pl="5px"
            key={value}
            value={value}
            onClick={() => {
              const updatedOptions = [...selectedOptions];
              const index = updatedOptions.indexOf(value);
              if (index >= 0) {
                updatedOptions.splice(index, 1);
              } else {
                updatedOptions.push(value);
              }
              setSelectedOptions(updatedOptions);
              column.setFilterValue(updatedOptions);
            }}
            closeOnSelect={false}
          >
            <FaIcon
              icon={selectedOptions.includes(value) ? 'square-check' : 'square'}
            />
            <Text ml="16px">{label}</Text>
          </MenuItem>
        );
      })}
    </Box>
  );
};

const DateFilters = ({
  selectedOptions,
  setSelectedOptions,
  column,
}: {
  selectedOptions: string[];
  column: Column<any, unknown>;
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  return (
    <DateRangePicker
      onSelectDateRange={(newDateRange) => {
        const [newStartDate, newEndDate] = newDateRange;
        if (newStartDate && newEndDate) {
          const startFullDateStr = commonDate.dateToFullDateString(
            newStartDate,
          );
          const endFullDateStr = commonDate.dateToFullDateString(newEndDate);
          const updatedOptions = [startFullDateStr, endFullDateStr];
          setSelectedOptions(updatedOptions);
          column.setFilterValue(updatedOptions);
        }
      }}
      currentDateRange={{
        chartDataStartDate: selectedOptions[0]
          ? commonDate.fullDateStrToDate(selectedOptions[0] as Util.FullDateStr)
          : null,
        chartDataEndDate: selectedOptions[1]
          ? commonDate.fullDateStrToDate(selectedOptions[1] as Util.FullDateStr)
          : null,
      }}
      width="100%"
    />
  );
};

export const customMultiFilter = (
  row: Row<any>,
  id: string,
  filterValues: Util.Primitive[],
) => {
  const rowValue: Util.Primitive = row.getValue(id);
  if (filterValues.length === 0 || filterValues.includes(rowValue)) {
    return true;
  }
  return false;
};

export const customFullDateStrFilter = (
  row: Row<any>,
  id: string,
  filterValues: Util.Primitive[],
) => {
  const rowValue: Util.Primitive = row.getValue(id);
  const [start, end] = filterValues;

  if (!start && !end) {
    return true;
  }
  if (!rowValue) return false;
  if (start && rowValue < start) return false;
  if (end && rowValue > end) return false;

  return true;
};

export const getFilterValue = (column: Column<any, unknown>): string[] => {
  const val = column.getFilterValue();
  if (!val) return [];

  const array = Object.values(val);
  return array.map((v: any) => _.toString(v));
};

/**
 * ColumnFilter component to be used in tandem with TanStack to create dropdown filter menus
 *
 * @param type: default checkbox returns an array of selected options using checkbox as the grouping, date returns 2 element array of [start,end] using date selectors
 * @param header: header of filter menu
 * @param valueFormatFn: optional function to format option display values
 * @param options: optional values for the labels in the filter, otherwise the column values are used
 */
export const ColumnFilter = ({
  type = 'checkbox',
  header,
  column,
  valueFormatFn,
  options,
}: {
  type?: 'checkbox' | 'date';
  header: string;
  column: Column<any, unknown>;
  options?: { label: string | React.ReactNode; value: any }[];
  valueFormatFn?: (value: any) => string;
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    getFilterValue(column),
  );

  const optionValues = useMemo(() => {
    if (options) return options;

    const sortedUniqueValues = Array.from(
      column.getFacetedUniqueValues().keys(),
    ).sort();

    return _.map(sortedUniqueValues, (value) => ({
      label: valueFormatFn
        ? valueFormatFn(value)
        : commonUtils.toTitleCase(value),
      value,
    }));
  }, [options, column, valueFormatFn]);

  return (
    <Menu
      matchWidth
      autoSelect={false}
      eventListeners={{ scroll: false, resize: true }}
    >
      <MenuButton fontSize="16px" color={colors.aurora[400]}>
        <Flex alignItems="left" flexDirection="row">
          {_.compact(selectedOptions).length > 0 && (
            <Text ml="5px">| {_.compact(selectedOptions).length}</Text>
          )}
          <FaIcon icon="chevron-down" style={{ marginLeft: '16px' }} />
        </Flex>
      </MenuButton>
      <MenuList
        p="16px"
        maxW="800"
        left={0}
        color={colors.aurora[400]}
        backgroundColor={colors.millet[50]}
        fontSize="16px"
      >
        <Heading
          fontSize="sm"
          fontWeight="medium"
          mb="16px"
          textTransform="none"
        >
          {header}
        </Heading>
        {type === 'checkbox' && (
          <CheckboxFilters
            optionValues={optionValues}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            column={column}
          />
        )}
        {type === 'date' && (
          <DateFilters
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            column={column}
          />
        )}
        <Divider width="90%" margin="16px auto" />
        <Box>
          <Button
            borderColor={colors.aurora[400]}
            color={colors.aurora[400]}
            h="1.75rem"
            size="sm"
            w="80%"
            variant="outline"
            fontWeight="medium"
            onClick={() => {
              setSelectedOptions([]);
              column.setFilterValue([]);
            }}
          >
            Clear
          </Button>
        </Box>
      </MenuList>
    </Menu>
  );
};
